import { AnimatePresence, motion } from 'motion/react'
import { TASK_DETAILS_LAYOUT } from '../constants'
import { useOpenTaskPage } from '../hooks/useOpenTaskPage'
import { TaskDetails } from './TaskDetails'
import type { TaskDetailsPanelProps } from '../type'
import { cn } from '@/lib/utils'

export function TaskDetailsPanel({
  task,
  parentTask = null,
  statuses,
  className,
  emptyState,
  onSubTaskClick,
  onParentTaskClick,
  position,
  onNavigateUp,
  onNavigateDown,
  canNavigateUp,
  canNavigateDown,
  onClose,
}: TaskDetailsPanelProps) {
  const openTaskPage = useOpenTaskPage()
  if (!task) {
    return (
      <div
        data-testid="task-details-panel"
        data-state="empty"
        className={cn(
          'flex h-full items-center justify-center p-6 text-sm text-muted-foreground',
          className,
        )}
      >
        {emptyState ?? 'Select a task to view its details.'}
      </div>
    )
  }

  return (
    <div
      data-testid="task-details-panel"
      data-state="loaded"
      data-task-id={task.id}
      // A white card inset in the paper, like the list beside it (design 30).
      className={cn('flex h-full min-h-0 flex-col bg-background p-2', className)}
    >
      <div className="flex-1 min-h-0 overflow-hidden rounded-xl border bg-card">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={task.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="flex h-full max-h-full w-full"
          >
            <TaskDetails
              task={task}
              statuses={statuses}
              parentTask={parentTask}
              layout={TASK_DETAILS_LAYOUT.HORIZONTAL}
              onSubTaskClick={onSubTaskClick}
              onParentTaskClick={onParentTaskClick}
              chrome={{
                position,
                onNavigateUp,
                onNavigateDown,
                canNavigateUp,
                canNavigateDown,
                onExpand: () => openTaskPage(task.id),
                onClose,
              }}
            />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

export default TaskDetailsPanel
